import React from 'react';
import { Box, Container, Stack, Typography, Paper } from '@mui/material';
import { Icon } from '@iconify/react';

interface FeatureItemProps {
  icon: string;
  title: string;
  description: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  icon,
  title,
  description,
}) => (
  <Paper
    elevation={0}
    sx={{
      background: 'rgba(255, 255, 255, 0.05)',
      padding: 3,
      textAlign: 'center',
      borderRadius: '16px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
      },
    }}
  >
    <Icon
      icon={icon}
      width='50'
      height='50'
      style={{ color: 'primary.main', marginBottom: '16px' }}
    />
    <Typography
      variant='h6'
      component='h3'
      sx={{ fontWeight: 600, mb: 1, color: '#fff' }}
    >
      {title}
    </Typography>
    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
      {description}
    </Typography>
  </Paper>
);

const FeaturesSection: React.FC = () => {
  const features = [
    {
      icon: 'mdi:web-check',
      title: 'Web Development',
      description:
        'Creating stunning and responsive websites tailored to your needs.',
    },
    {
      icon: 'mdi:cellphone-link',
      title: 'Mobile Apps',
      description: 'Building native and cross-platform mobile applications.',
    },
    {
      icon: 'mdi:briefcase-outline',
      title: 'Business Development',
      description:
        'Driving growth through strategic partnerships, market expansion, and client relationship management.',
    },
    {
      icon: 'mdi:chart-line',
      title: 'Business Intelligence',
      description: 'Turning data into actionable insights for business growth.',
    },
  ];

  return (
    <Box
      id='services'
      sx={{
        background: (theme) => theme.palette.background.paper,
        py: { xs: 6, md: 10 },
        color: '#fff',
      }}
    >
      <Container maxWidth='lg'>
        <Typography
          variant='h4'
          component='h2'
          sx={{ textAlign: 'center', fontWeight: 700, mb: 6 }}
        >
          Our Core Services
        </Typography>
        {/* Using Stack for layout instead of Grid */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={4}
          justifyContent='center'
          flexWrap='wrap' // Allow wrapping on smaller screens
        >
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                width: {
                  xs: '100%',
                  sm: 'calc(50% - 16px)',
                  md: 'calc(25% - 24px)',
                }, // Adjust width based on spacing
                mb: { xs: 4, sm: 0 }, // Add margin bottom on extra small screens
              }}
            >
              <FeatureItem {...feature} />
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
